/**
 * Expediente del Préstamo — lista general (vista 360, solo lectura).
 *
 * Todos los préstamos del tenant con indicadores calculados (balance,
 * cuotas pagadas, atraso). Al hacer clic en un préstamo se abre su
 * expediente en URL propia: /prestamos/expediente/:noprest.
 */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import { DataGrid } from "@mui/x-data-grid";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { estadoPrestamo } from "lib/prestamos";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin préstamos que mostrar
      </Typography>
    </Stack>
  );
}

export default function Expediente() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [estado, setEstado] = useState("A");
  const [gestorFiltro, setGestorFiltro] = useState("todos");

  useEffect(() => {
    let activo = true;
    setLoading(true);
    post("/api/expediente_prestamos", {})
      .then(({ ok, data }) => {
        if (!activo) return;
        if (ok && Array.isArray(data.prestamos)) {
          setRows(data.prestamos.map((p) => ({ id: p.noprest, ...p })));
        } else {
          showAlert({
            message: data.Error || "No se pudieron cargar los préstamos",
            color: "error",
          });
        }
      })
      .finally(() => activo && setLoading(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    // El filtro usa el estado calculado: un préstamo pagado por completo
    // queda con status 'C' en la BD igual que un cancelado manual, y aquí
    // se separa por el balance pendiente (ver lib/prestamos).
    const porEstado = { A: "activo", S: "saldado", C: "cancelado", I: "incobrable" };
    return rows.filter((r) => {
      if (estado !== "todos" && estadoPrestamo(r.status, r.balance) !== porEstado[estado])
        return false;
      if (gestorFiltro !== "todos" && (r.gestor || "") !== gestorFiltro) return false;
      if (!q) return true;
      return `${r.noprest} ${r.nombres} ${r.cedula} ${r.chasis} ${r.marca} ${r.modelo}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, busqueda, estado, gestorFiltro]);

  // Gestores presentes en la cartera (para el filtro). "" = sin gestor.
  const gestores = useMemo(() => {
    const s = new Set(rows.map((r) => r.gestor || "").filter(Boolean));
    return [...s].sort();
  }, [rows]);

  const columns = [
    { field: "noprest", headerName: "No.", width: 90 },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 190 },
    { field: "cedula", headerName: "Cédula", width: 140 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      width: 170,
      valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
    },
    { field: "fecha", headerName: "Fecha", width: 110 },
    {
      field: "gestor",
      headerName: "Gestor",
      width: 120,
      valueGetter: (value) => value || "",
    },
    {
      field: "deuda",
      headerName: "Deuda",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "balance",
      headerName: "Balance",
      width: 120,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "cuotas",
      headerName: "Cuotas",
      width: 100,
      align: "center",
      headerAlign: "center",
      valueGetter: (_v, row) => `${row.cuotaspagadas}/${row.cuotastotales}`,
    },
    {
      field: "cuotasatrasadas",
      headerName: "Atraso",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value > 0 ? (
          <Chip
            size="small"
            label={`${params.value} cuotas`}
            sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Al día"
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "status",
      headerName: "Estado",
      width: 110,
      renderCell: (params) => {
        const e = estadoPrestamo(params.value, params.row.balance);
        if (e === "activo")
          return (
            <Chip
              size="small"
              label="Activo"
              sx={{ bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 }}
            />
          );
        if (e === "saldado")
          return (
            <Chip
              size="small"
              label="Saldado"
              sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
            />
          );
        if (e === "incobrable")
          return (
            <Chip
              size="small"
              label="Incobrable"
              sx={{ bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 }}
            />
          );
        return (
          <Chip
            size="small"
            label="Cancelado"
            sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
          />
        );
      },
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Expediente del Préstamo"
        subtitle="Todos los financiamientos con su estado — haz clic en uno para ver el expediente completo"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Buscar (cliente, cédula, chasis, número...)"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Estado"
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="A">Activos</MenuItem>
                <MenuItem value="S">Saldados</MenuItem>
                <MenuItem value="C">Cancelados</MenuItem>
                <MenuItem value="I">Incobrables</MenuItem>
                <MenuItem value="todos">Todos</MenuItem>
              </TextField>
            </Grid>
            {gestores.length > 0 && (
              <Grid item xs={12} sm={3} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Gestor"
                  value={gestorFiltro}
                  onChange={(e) => setGestorFiltro(e.target.value)}
                  sx={fpInputSx}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  {gestores.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sm={3}
              md={4}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {filtradas.length} préstamos
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={filtradas}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px", cursor: "pointer" }}
              disableRowSelectionOnClick
              onRowClick={(params) => navigate(`/prestamos/expediente/${params.row.noprest}`)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
